<script lang="ts">
    import { onMount, onDestroy } from 'svelte';
    // import { makeLibrarySongsRequestItemsIntent } from '~/jet/intents/library/songs/LibrarySongsRequestItemsIntent';
    import VirtualScrollList from '@amp/web-app-components/src/components/VirtualScrollList/VirtualScrollList.svelte';
    import type { Section } from '@jet-app/music-classical/models/section/Section';
    import type { TrackLockup } from '~/jet/models';
    import { scrollIsLocked } from '@amp/web-app-components/src/stores/scroll-is-locked';
    import { loggerFor } from '@amp/web-apps-logger';
    import { getJet } from '~/jet';
    import { getItemSelector } from '~/stores/item-selector';
    import { showFooter } from '~/stores/show-footer';
    // import allowDrag from '@amp/web-app-components/src/actions/allow-drag';
    import TrackListHeader from './TrackListHeader.svelte';
    import TrackListItem from './TrackListItem.svelte';
    // import { fetchInLibraryStatusForSections } from '~/utils/fetch-in-library';
    // import { getCommerce } from '~/utils/commerce';
    // import { getLocalLibraryState } from '~/utils/local-library-state';
    // import { getLocale } from '~/utils/locale';
    import { makePaginationIntent } from '~/jet/intents';

    const componentIdentifier = Symbol();
    const AMOUNT_TO_FETCH = 20;
    const ITEM_HEIGHT = 60;
    const BUFFER_HEIGHT = 1000;
    const log = loggerFor('<Tracks>');
    const jet = getJet();
    const itemSelector = getItemSelector();
    // const localLibraryState = getLocalLibraryState();
    // const locale = getLocale();
    // const { state: commerceState } = getCommerce();

    export let section: Section<TrackLockup>;

    $: items = section?.items;
    $: totalSongs = section?.pageMetadata?.total;
    $: itemSelector.addItems(componentIdentifier, items);
    // $: draggedItems = $itemSelector.selectedItems.includes(
    //     $itemSelector.currentMouseTarget,
    // )
    //     ? $itemSelector.selectedItems
    //     : [$itemSelector.currentMouseTarget];
    // $: draggedDescriptors = draggedItems.map(
    //     (song: TrackLockup) => song?.contentDescriptor,
    // );

    async function addNewItems(e: CustomEvent<{ items: TrackLockup[] }>) {
        if (e.detail) {
            const { items } = e.detail;
            itemSelector.addItems(componentIdentifier, items);

            // const paginatedSections = [{ items } as Section<LibrarySongs>];
            // await fetchInLibraryStatusForSections(
            //     paginatedSections,
            //     $commerceState.user?.subscriberType,
            //     jet,
            //     log,
            //     locale,
            //     localLibraryState,
            // );
        }
    }
    onMount(() => {
        scrollIsLocked.set(true);
        showFooter.set(false);
    });

    onDestroy(() => {
        scrollIsLocked.set(false);
        itemSelector.resetItems(componentIdentifier);
        showFooter.set(true);
    });
</script>

<div class="songs-container" data-testid="library-songs-component">
    <TrackListHeader />

    <!-- fetchIntentFn={makeLibrarySongsRequestItemsIntent} -->
    <VirtualScrollList
        {items}
        itemCount={totalSongs}
        amountToFetch={AMOUNT_TO_FETCH}
        itemHeight={ITEM_HEIGHT}
        bufferHeight={BUFFER_HEIGHT}
        fetchIntentFn={makePaginationIntent}
        bottomSpacerHidden={false}
        restrainContainerWidth={false}
        {log}
        {jet}
        useManualPagination={false}
        pageMetadata={section?.pageMetadata}
        overrideKeydown={itemSelector.handleKeydown}
        setAriaAttributes={true}
        on:fetchComplete={addNewItems}
        use={[
            // [
            //     allowDrag,
            //     {
            //         dragEnabled: true,
            //         dragData: { items: draggedDescriptors },
            //         badgeCount: draggedDescriptors.length,
            //         dragImage:
            //             '[data-current-mouse-target="true"] .artwork-component picture',
            //     },
            // ],
        ]}
    >
        <svelte:fragment
            slot="itemComponent"
            let:item
            let:itemIdx
            let:visibleItems
        >
            <TrackListItem
                {item}
                items={visibleItems}
                rowIndex={itemIdx}
                isFirst={itemIdx === 0}
                isLast={itemIdx === totalSongs - 1}
            />
        </svelte:fragment>

        <div
            slot="placeholder"
            class="song-placeholder"
            class:song-placeholder--alt={itemIdx % 2 === 0}
            let:itemIdx
        />
    </VirtualScrollList>
</div>

<style lang="scss">
    @use '@amp/web-shared-styles/sasskit-stylekit/ac-sasskit-config';
    @use '@amp/web-shared-styles/app/core/globalvars' as *;
    @use '@amp/web-shared-styles/app/core/mixins/after-shadow' as *;
    @use '@amp/web-shared-styles/app/core/mixins/aspect-ratio' as *;
    @use '@amp/web-shared-styles/app/core/mixins/tabular-numbers' as *;
    @use 'amp/stylekit/core/mixins/overflow-bleed' as *;
    @use 'amp/stylekit/core/mixins/line-clamp' as *;

    .songs-container {
        --fixedHeaderHeight: 125px; // Height of the section header + columns header
        --libraryTrackHeaderHeight: 40px; // Height of columns header
        --libraryTrackWidth: calc(100% - var(--bodyGutter) * 2);

        // Track list columns are defined here, then shared with TrackListHeader
        // - First column: prefix column, currently unused in library tracks
        // - Middle columns: equal-sized metadata columns
        // - Last column: time and context menu
        --libraryTrackColumns: 0 repeat(var(--libraryTrackEqualColumns, 1), 1fr)
            90px;
        display: contents;

        @media (--small) {
            // In `small` viewport, show more metadata columns
            --libraryTrackEqualColumns: 3;

            :global(.songs-list__col--tertiary) {
                display: table-cell;
            }
        }

        :global(.virtual-rows) {
            margin-inline: auto;
        }

        :global(.virtual-row) {
            width: var(--libraryTrackWidth);
            margin-inline: var(--bodyGutter);
        }

        // Track list row style overrides start below.
        //
        // These are defined here rather than in <TrackListItem /> because
        // we can't wrap the shared component lockup in a wrapper div
        // without breaking VoiceOver in Safari. See: rdar://144260535
        :global(.songs-list-row) {
            height: var(--rowHeight, 60px);
            display: grid;
            align-items: center;
            border-radius: $global-border-radius-small;
            grid-template-columns: var(--libraryTrackColumns);
        }

        :global(.songs-list-row__song-name-wrapper),
        :global(.songs-list__song-link-wrapper) {
            // Override white-space to allow text to break to two lines
            white-space: normal;

            @include line-clamp(2);
            // stylelint-disable-next-line length-zero-no-unit
            @include overflow-bleed(0px);
        }

        :global(.songs-list__col--secondary),
        :global(.songs-list__col--tertiary),
        :global(.songs-list__col--time) {
            padding-inline-start: 9px;
        }

        :global(.songs-list__col--time) {
            justify-self: end;
        }

        :global(.artwork-component) {
            border-radius: $global-border-radius-small;

            @include aspect-ratio(40px);
        }

        :global(.songs-list-row__controls) {
            column-gap: 6px;

            @include tabular-numbers;
        }

        :global(
                .songs-list-row:not(.songs-list-row--selected)
                    .songs-list-row__controls
            ) {
            --contextMenuEllipsisFillOverride: var(--systemSecondary);
        }
    }

    .song-placeholder {
        height: var(--rowHeight, 60px);
        border-radius: $global-border-radius-small;
    }

    .song-placeholder--alt {
        background: var(--tracklistAltRowColor);
    }
</style>
